import { AnalisisLaboratorioResponse } from "../modulos/fertilizacion/response/AnalisisLaboratorioResponse";
import { AnalisisNutrienteResponse } from "../modulos/fertilizacion/response/AnalisisNutrienteResponse";
import { AnalisisRegistroResponse } from "../modulos/fertilizacion/response/AnalisisRegistroResponse";
import {
  NitrofertPlan,
  NitrofertPlanProducto,
  parseNitrofertPlan,
} from "../modulos/fertilizacion/response/NitrofertPlan";

/** Claves de `nutrients_supplied` (L3, minúscula) → `nutrient` de
 *  `AnalisisNutrienteResponse`. **No es un `toUpperCase()`**: el azufre viaja
 *  como `s` en el plan y como `SO3` en los nutrientes. */
const SUPPLIED_A_NUTRIENTE: Record<string, string> = {
  n: "N",
  p2o5: "P2O5",
  k2o: "K2O",
  cao: "CaO",
  mgo: "MgO",
  s: "SO3",
  so3: "SO3",
  zn: "Zn",
  cu: "Cu",
  b: "B",
  fe: "Fe",
  mn: "Mn",
  mo: "MO",
};

/** Traduce una clave de `nutrients_supplied`/`coverage` al `nutrient` tipado.
 *  Devuelve la clave cruda si no está mapeada (no se pierde información). */
export function nutrienteDesdeSupplied(clave: string): string {
  return SUPPLIED_A_NUTRIENTE[clave.toLowerCase()] ?? clave;
}

/** Urgencias de menor a mayor prioridad de compra. */
const URGENCIA_ORDEN: Record<string, number> = {
  immediate: 0,
  this_season: 1,
  next_season: 2,
};

/** Urgencia más baja: la que se usa cuando un producto no aporta ningún
 *  nutriente deficiente. */
const URGENCIA_DEFECTO = "next_season";

/** Etiquetas en español de la urgencia. */
const URGENCIA_LABEL: Record<string, string> = {
  immediate: "Inmediato",
  this_season: "Esta temporada",
  next_season: "Próxima temporada",
};

/** Etiqueta en español de una urgencia (fallback: la clave cruda). */
export function urgenciaLabel(urgency: string): string {
  return URGENCIA_LABEL[urgency] ?? urgency;
}

/** `true` si `a` es más urgente que `b`. */
function esMasUrgente(a: string, b: string): boolean {
  const ordenA = URGENCIA_ORDEN[a] ?? Number.MAX_SAFE_INTEGER;
  const ordenB = URGENCIA_ORDEN[b] ?? Number.MAX_SAFE_INTEGER;
  return ordenA < ordenB;
}

/** Peso de una urgencia para ordenar listas (menor = antes). */
export function urgenciaOrden(urgency: string): number {
  return URGENCIA_ORDEN[urgency] ?? Number.MAX_SAFE_INTEGER;
}

/** Requerimiento agregado de un óxido en toda la finca. */
export type OxidoAgregado = {
  /** Nutriente en óxido (`N`, `K2O`, `CaO`…). */
  nutrient: string;
  /** `chemical` | `organic_amendment` | `liming`. */
  nutrientType: string;
  /** Kilos totales sumados sobre todos los lotes. */
  kgTotal: number;
  /** La urgencia más alta entre los lotes que lo requieren. */
  urgency: string;
  /** Cuántos lotes lo requieren. */
  lotes: number;
  /** El `tier` más estructural entre los lotes (para ordenar). */
  tier: number;
};

/** Agrega los nutrientes de todos los registros por óxido, **sumando kg
 *  absolutos** (nunca promediando). Es la lista genérica "sin marca": lo que
 *  necesita la finca expresado en óxidos. */
export function agregarPorOxido(
  registros: AnalisisRegistroResponse[],
  filtro?: (nutriente: AnalisisNutrienteResponse) => boolean,
): OxidoAgregado[] {
  const porOxido = new Map<string, OxidoAgregado>();
  for (const registro of registros) {
    for (const nutriente of registro.nutrientes) {
      if (filtro && !filtro(nutriente)) continue;
      const actual = porOxido.get(nutriente.nutrient);
      if (!actual) {
        porOxido.set(nutriente.nutrient, {
          nutrient: nutriente.nutrient,
          nutrientType: nutriente.nutrientType,
          kgTotal: nutriente.kgTotal,
          urgency: nutriente.urgency,
          lotes: 1,
          tier: nutriente.tier,
        });
        continue;
      }
      actual.kgTotal += nutriente.kgTotal;
      actual.lotes += 1;
      actual.tier = Math.min(actual.tier, nutriente.tier);
      if (esMasUrgente(nutriente.urgency, actual.urgency)) {
        actual.urgency = nutriente.urgency;
      }
    }
  }
  return [...porOxido.values()].sort(
    (a, b) => a.tier - b.tier || b.kgTotal - a.kgTotal,
  );
}

/** Urgencia de un producto = la más alta entre los óxidos **deficientes** que
 *  aporta. Los óxidos incidentales (que el producto trae pero la finca no
 *  necesita) se ignoran; si no aporta ninguno requerido, cae a la urgencia más
 *  baja. Se cruza contra los nutrientes tipados, **no** contra
 *  `nitrofertPlan.l2_nutrients`, que puede venir vacío o solo con cal/MO. */
export function urgenciaProducto(
  producto: NitrofertPlanProducto,
  urgenciaPorOxido: Map<string, string>,
): string {
  let urgencia: string | null = null;
  for (const clave of Object.keys(producto.nutrientsSupplied)) {
    const requerida = urgenciaPorOxido.get(nutrienteDesdeSupplied(clave));
    if (!requerida) continue;
    if (urgencia === null || esMasUrgente(requerida, urgencia)) {
      urgencia = requerida;
    }
  }
  return urgencia ?? URGENCIA_DEFECTO;
}

/** Índice óxido → urgencia requerida, para `urgenciaProducto`. */
export function urgenciaPorOxido(
  registros: AnalisisRegistroResponse[],
): Map<string, string> {
  const mapa = new Map<string, string>();
  for (const agregado of agregarPorOxido(registros)) {
    mapa.set(agregado.nutrient, agregado.urgency);
  }
  return mapa;
}

/** Un grupo con su plan comercial ya parseado. */
export type GrupoConPlan = {
  /** Id del grupo. */
  id: string;
  /** Etiqueta del microservicio (ej. "exact_ASU-11660-2024"). */
  groupId: string;
  /** Área total del grupo (ha). */
  totalAreaHa: number;
  /** Plan parseado, o `null` si el grupo no tiene o L3 falló. */
  plan: NitrofertPlan | null;
  /** Mensaje de error de L3, si lo hubo. */
  l3Error: string | null;
  /** Registros (lotes) que integran el grupo. */
  registros: AnalisisRegistroResponse[];
};

/** Los grupos del análisis con su `nitrofertPlan` ya parseado y sus registros
 *  resueltos. Vacío en modo Sextante. */
export function gruposConPlan(
  analisis: AnalisisLaboratorioResponse,
): GrupoConPlan[] {
  return analisis.grupos.map((grupo) => ({
    id: grupo.id,
    groupId: grupo.groupId,
    totalAreaHa: grupo.totalAreaHa,
    plan: parseNitrofertPlan(grupo.nitrofertPlan),
    l3Error: grupo.l3Error,
    registros: analisis.registros.filter(
      (registro) => registro.grupoId === grupo.id,
    ),
  }));
}

/** Un óxido que el portafolio Nitrofert no cubre (del todo o en parte). */
export type FaltanteOxido = {
  /** Nutriente en óxido. */
  nutrient: string;
  /** Kilos que faltan conseguir por fuera. */
  kgFaltante: number;
  /** `true` si Nitrofert no tiene ningún producto adecuado; `false` si lo cubre
   *  parcialmente (tope de dosis). */
  sinProducto: boolean;
  /** Porcentaje que sí cubre el portafolio (0 cuando `sinProducto`). */
  coberturaPct: number;
};

/** Óxidos que quedan sin cubrir en toda la finca, sumando **kg absolutos** por
 *  grupo (nunca promediando porcentajes).
 *
 *  Recorre `coverage` completo — todo lo que esté por debajo de 100 — porque un
 *  parcial por tope de dosis no aparece en `uncovered`. Un óxido en
 *  `coverage: 0` está en las dos listas y se reporta una sola vez, como
 *  "sin producto". */
export function faltantesPorOxido(grupos: GrupoConPlan[]): FaltanteOxido[] {
  const acumulado = new Map<
    string,
    { kgFaltante: number; kgRequerido: number; sinProducto: boolean }
  >();

  for (const grupo of grupos) {
    if (!grupo.plan) continue;
    const requerido = new Map(
      agregarPorOxido(grupo.registros).map((agregado) => [
        agregado.nutrient,
        agregado.kgTotal,
      ]),
    );
    const sinProducto = new Set(
      grupo.plan.uncovered.map((clave) => nutrienteDesdeSupplied(clave)),
    );

    // Todo `coverage` por debajo de 100, más los `uncovered` que ni figuren ahí.
    const claves = new Set([
      ...Object.keys(grupo.plan.coverage).map(nutrienteDesdeSupplied),
      ...sinProducto,
    ]);

    for (const nutrient of claves) {
      const pct = _coberturaDe(grupo.plan, nutrient);
      if (pct >= 100) continue;
      const kgRequerido = requerido.get(nutrient) ?? 0;
      if (kgRequerido <= 0) continue;

      const actual = acumulado.get(nutrient) ?? {
        kgFaltante: 0,
        kgRequerido: 0,
        sinProducto: false,
      };
      actual.kgFaltante += kgRequerido * (1 - pct / 100);
      actual.kgRequerido += kgRequerido;
      actual.sinProducto = actual.sinProducto || sinProducto.has(nutrient);
      acumulado.set(nutrient, actual);
    }
  }

  return [...acumulado.entries()]
    .map(([nutrient, datos]) => ({
      nutrient,
      kgFaltante: datos.kgFaltante,
      sinProducto: datos.sinProducto,
      coberturaPct:
        datos.kgRequerido > 0
          ? Math.round((1 - datos.kgFaltante / datos.kgRequerido) * 100)
          : 0,
    }))
    .sort((a, b) => b.kgFaltante - a.kgFaltante);
}

/** Cobertura (%) de un óxido en un plan, traduciendo las claves de `coverage`.
 *  100 si el óxido no figura (no se pidió, no falta nada). */
function _coberturaDe(plan: NitrofertPlan, nutrient: string): number {
  for (const [clave, pct] of Object.entries(plan.coverage)) {
    if (nutrienteDesdeSupplied(clave) === nutrient) return pct;
  }
  // Está en `uncovered` pero no en `coverage`: no se cubre nada.
  return plan.uncovered.some(
    (clave) => nutrienteDesdeSupplied(clave) === nutrient,
  )
    ? 0
    : 100;
}
